import { Viewer, type ViewerRef } from '@boostbrothers/wysiwyg';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import GraphicPreviewAppbar from '~assets/graphics/graphic-preview-appbar.png';
import GraphicPreviewStatusbar from '~assets/graphics/graphic-preview-statusbar.png';
import IconAuthorProfile from '~assets/icons/icon-author-profile.svg?react';
import { isEmpty } from '~utils/editor';

const MetricIcon = ({ type }: { type: 'view' | 'like' | 'comment' }) => {
  if (type === 'view') {
    return (
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }

  if (type === 'like') {
    return (
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M20.8 4.6a5.4 5.4 0 0 0-7.6 0L12 5.8l-1.2-1.2a5.4 5.4 0 0 0-7.6 7.6L12 21l8.8-8.8a5.4 5.4 0 0 0 0-7.6Z" />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M21 11.5a8.4 8.4 0 0 1-8.8 8.3 9.1 9.1 0 0 1-3.8-.8L3 20.5l1.5-4.3a8.1 8.1 0 0 1-1.1-4.1A8.4 8.4 0 0 1 12.2 4 8.4 8.4 0 0 1 21 11.5Z" />
      <path d="M8.5 12h.01M12 12h.01M15.5 12h.01" />
    </svg>
  );
};

const getPlainTextFromHtml = (html: string) => {
  if (!html) return '';

  const htmlWithBlockSpacing = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, ' ');

  if (typeof DOMParser === 'undefined') {
    return htmlWithBlockSpacing
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(
    htmlWithBlockSpacing,
    'text/html'
  );

  return (parsedDocument.body.textContent || '').replace(/\s+/g, ' ').trim();
};

interface PreviewProps {
  title: string;
  document: string;
  previewText: string;
  thumbnail: {
    url: string;
    fileName: string;
  };
  authorNickname: string;
}

const Preview = forwardRef<ViewerRef, PreviewProps>(
  ({ title, document, previewText, thumbnail, authorNickname }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const hasThumbnail = !!thumbnail.url.trim();
    const documentPreviewText = useMemo(
      () => getPlainTextFromHtml(document),
      [document]
    );
    const previewTextFallback = previewText.trim() || documentPreviewText;
    const hasPreviewText = !!previewTextFallback;

    useImperativeHandle(ref, () => ({
      scrollToNode: (nodeIndex: number) => {
        const proseMirror = containerRef.current?.querySelector('.ProseMirror');
        if (!proseMirror) return;

        const children = proseMirror.children;
        const targetNode = children[nodeIndex] as HTMLElement | undefined;

        if (targetNode) {
          targetNode.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      },
    }));

    return (
      <div className="flex w-[360px] flex-col gap-4">
        {hasThumbnail ? (
          <section className="box-border overflow-hidden rounded-3xl border border-[#E2E6EA] bg-white shadow-[0_12px_24px_rgba(44,55,68,0.08)]">
            <div className="relative h-[205px] overflow-hidden">
              <img
                src={thumbnail.url}
                alt={thumbnail.fileName || 'thumbnail preview'}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
            </div>
            <div className="px-5 pb-5 pt-4">
              <h2
                className={`mb-5 line-clamp-2 text-base font-bold leading-6 break-words whitespace-pre-wrap ${title.trim() ? 'text-CoolGray-900' : 'text-CoolGray-400'}`}
              >
                {title.trim() ? title : '타이틀을 입력해주세요.'}
              </h2>
              <div className="flex items-center justify-between gap-4 text-CoolGray-400">
                <div className="flex min-w-0 items-center gap-2">
                  <IconAuthorProfile className="h-5 w-5 shrink-0" />
                  <span className="truncate text-[13px]">
                    {authorNickname || '이름'}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="flex items-center gap-1 text-[13px] leading-none">
                    <MetricIcon type="view" />
                    25
                  </span>
                  <span className="flex items-center gap-1 text-[13px] leading-none">
                    <MetricIcon type="like" />
                    16
                  </span>
                  <span className="flex items-center gap-1 text-[13px] leading-none">
                    <MetricIcon type="comment" />4
                  </span>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="box-border rounded-3xl border border-[#E2E6EA] bg-[radial-gradient(circle_at_45%_45%,#F2FFD6_0%,#FAFFF0_34%,#FFFFFF_72%)] p-5 shadow-[0_12px_24px_rgba(44,55,68,0.08)]">
            <div className="mb-4 flex min-w-0 items-center gap-2 text-CoolGray-400">
              <IconAuthorProfile className="h-5 w-5 shrink-0" />
              <span className="truncate text-[13px]">
                {authorNickname || '이름'}
              </span>
            </div>

            <h2
              className={`mb-3 line-clamp-2 text-[22px] font-bold leading-[28px] break-words whitespace-pre-wrap ${title.trim() ? 'text-CoolGray-900' : 'text-CoolGray-400'}`}
            >
              {title.trim() ? title : '타이틀을 입력해주세요.'}
            </h2>

            <p
              className={`line-clamp-2 min-h-[36px] text-sm leading-[18px] break-words ${hasPreviewText ? 'text-CoolGray-600' : 'text-CoolGray-400'}`}
            >
              {hasPreviewText ? previewTextFallback : '머리글을 입력해주세요.'}
            </p>

            <div className="mt-5 flex items-center gap-4 text-CoolGray-400">
              <span className="flex items-center gap-1 text-[13px] leading-none">
                <MetricIcon type="view" />
                25
              </span>
              <span className="flex items-center gap-1 text-[13px] leading-none">
                <MetricIcon type="like" />
                16
              </span>
              <span className="flex items-center gap-1 text-[13px] leading-none">
                <MetricIcon type="comment" />4
              </span>
            </div>
          </section>
        )}

        <div className="relative box-border h-[720px] w-[360px] overflow-hidden rounded-3xl border-8 border-[#FFFFFF/16] shadow-[0_29px_27.3px_0_rgba(0,0,0,0.25)]">
          <img
            src={GraphicPreviewStatusbar}
            alt="preview"
            className="absolute left-0 top-0"
          />
          <section
            ref={containerRef}
            className="mt-[49px] h-[calc(100%-49px)] overflow-y-scroll overscroll-contain scrollbar-hide bg-white"
          >
            <img src={GraphicPreviewAppbar} alt="preview" />
            <div className="mx-5 flex w-[calc(100%-40px)] flex-col gap-y-2 border-b-[2px] border-b-CoolGray-900 pb-8 pt-3">
              <h1
                className={`min-h-[28px] text-xl font-bold break-words whitespace-pre-wrap ${title.trim() ? 'text-CoolGray-900' : 'text-CoolGray-400'}`}
              >
                {title.trim() ? title : '타이틀을 입력해주세요.'}
              </h1>
              <div className="flex items-center gap-x-1">
                <IconAuthorProfile />
                <div className="flex min-w-0 items-center gap-x-2.5">
                  <span className="block min-w-0 max-w-[180px] truncate text-[13px] text-CoolGray-400">
                    {authorNickname}
                  </span>
                  <div className="h-3 w-[1px] bg-CoolGray-100" />
                  <span className="text-[13px] text-CoolGray-400">
                    YYYY.MM.DD
                  </span>
                </div>
              </div>
            </div>
            <div className={isEmpty(document) ? '[&_p]:text-CoolGray-400' : ''}>
              <Viewer
                value={
                  isEmpty(document) ? '본문 내용을 입력해주세요.' : document
                }
              />
            </div>
          </section>
        </div>
      </div>
    );
  }
);

export default Preview;
